import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getMedias } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';
import MediaGallery, { PRODUCT_DEFAULT_IMAGE } from '../../../modules/medias/public/MediaGallery';
import { filterMediasByKey, mediaFileUrl, newClientId } from './catalogCompositionUtils';

const SortableSelectedMedia = ({ row, disabled, onRemove, translate }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.media_id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const src = row.file_path ? mediaFileUrl(row.file_path) : PRODUCT_DEFAULT_IMAGE;
  return (
    <Paper ref={setNodeRef} style={style} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        {...attributes}
        {...listeners}
        sx={{ cursor: disabled ? 'default' : 'grab', display: 'flex', color: 'text.disabled' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Box
        component="img"
        src={src}
        alt=""
        sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, bgcolor: 'action.hover' }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {row.source === 'CATALOG'
            ? translate('catalog.media_source_catalog')
            : translate('catalog.media_source_product')}
        </Typography>
        <Typography variant="body2" noWrap>
          {row.filename || row.media_id}
        </Typography>
      </Box>
      {!disabled && (
        <Checkbox size="small" checked onChange={() => onRemove(row.media_id)} />
      )}
    </Paper>
  );
};

const CatalogItemMediaEditor = ({ item, productId, disabled, onChange }) => {
  const { translate } = useTranslation();
  const [productMedias, setProductMedias] = useState([]);
  const [catalogMedias, setCatalogMedias] = useState([]);

  const itemId = item?.id || '';

  const loadPools = useCallback(async () => {
    try {
      const res = await getMedias();
      const all = res?.data?.medias || [];
      if (productId) {
        setProductMedias(filterMediasByKey(all, 'product_image', productId));
      } else {
        setProductMedias([]);
      }
      if (itemId) {
        setCatalogMedias(filterMediasByKey(all, 'catalog_item_image', itemId));
      } else {
        setCatalogMedias([]);
      }
    } catch {
      setProductMedias([]);
      setCatalogMedias([]);
    }
  }, [productId, itemId]);

  useEffect(() => {
    void loadPools();
  }, [loadPools]);

  const selectedIds = useMemo(
    () => new Set((item?.medias || []).map((m) => m.media_id)),
    [item?.medias],
  );

  const mediaById = useMemo(() => {
    const map = new Map();
    [...productMedias, ...catalogMedias].forEach((m) => map.set(m.id, m));
    (item?.medias || []).forEach((m) => {
      if (!map.has(m.media_id)) {
        map.set(m.media_id, {
          id: m.media_id,
          file_path: m.file_path,
          mime_type: m.mime_type,
          filename: m.media_id,
        });
      }
    });
    return map;
  }, [productMedias, catalogMedias, item?.medias]);

  const selectedRows = useMemo(() => {
    const sorted = [...(item?.medias || [])].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    return sorted.map((m) => {
      const full = mediaById.get(m.media_id);
      return {
        ...m,
        file_path: m.file_path || full?.file_path,
        filename: full?.filename,
      };
    });
  }, [item?.medias, mediaById]);

  const mutateMedias = (updater) => {
    onChange((prev) => {
      const medias = prev?.medias || [];
      const nextMedias = updater(medias);
      return { ...prev, medias: nextMedias };
    });
  };

  const toggleProductMedia = (media, checked) => {
    mutateMedias((medias) => {
      let next = [...medias];
      if (checked) {
        if (!next.find((m) => m.media_id === media.id)) {
          next.push({
            id: newClientId(),
            media_id: media.id,
            source: 'PRODUCT',
            display_order: next.length,
            file_path: media.file_path,
            mime_type: media.mime_type,
          });
        }
      } else {
        next = next.filter((m) => m.media_id !== media.id);
      }
      return next.map((m, i) => ({ ...m, display_order: i }));
    });
  };

  const syncCatalogMedias = (uploaded) => {
    mutateMedias((medias) => {
      const uploadedIds = new Set((uploaded || []).map((m) => m.id));
      let next = medias.filter((m) => m.source !== 'CATALOG' || uploadedIds.has(m.media_id));
      (uploaded || []).forEach((m) => {
        if (!next.find((x) => x.media_id === m.id)) {
          next.push({
            id: newClientId(),
            media_id: m.id,
            source: 'CATALOG',
            display_order: next.length,
            file_path: m.file_path,
            mime_type: m.mime_type,
          });
        }
      });
      return next.map((m, i) => ({ ...m, display_order: i }));
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = selectedRows.findIndex((r) => r.media_id === active.id);
    const newIndex = selectedRows.findIndex((r) => r.media_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(selectedRows, oldIndex, newIndex).map((m, i) => ({
      ...m,
      display_order: i,
    }));
    mutateMedias(() => reordered);
  };

  const removeMediaById = (mediaId) => {
    const media = mediaById.get(mediaId);
    if (media) {
      toggleProductMedia(media, false);
      return;
    }
    mutateMedias((medias) =>
      medias.filter((m) => m.media_id !== mediaId).map((m, i) => ({ ...m, display_order: i })),
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {translate('catalog.item_medias_title')}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        {translate('catalog.item_medias_hint')}
      </Typography>

      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
            {translate('catalog.item_medias_selected')}
          </Typography>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={selectedRows.map((r) => r.media_id)} strategy={verticalListSortingStrategy}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedRows.map((row) => (
                  <SortableSelectedMedia
                    key={row.media_id}
                    row={row}
                    translate={translate}
                    disabled={disabled}
                    onRemove={removeMediaById}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        </Box>
      )}

      {productMedias.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1 }}>
            {translate('catalog.item_medias_from_product')}
          </Typography>
          <Grid container spacing={1}>
            {productMedias.map((media) => {
              const checked = selectedIds.has(media.id);
              const src = media.file_path ? mediaFileUrl(media.file_path) : PRODUCT_DEFAULT_IMAGE;
              return (
                <Grid item xs={4} sm={3} key={media.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 0.5,
                      borderColor: checked ? 'primary.main' : 'divider',
                      borderWidth: checked ? 2 : 1,
                    }}
                  >
                    <Box
                      component="img"
                      src={src}
                      alt=""
                      sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 0.5 }}
                    />
                    <FormControlLabel
                      sx={{ m: 0, px: 0.5 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={checked}
                          disabled={disabled}
                          onChange={(e) => toggleProductMedia(media, e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption" noWrap>{media.filename}</Typography>}
                    />
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {itemId && (
        <Box>
          <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1 }}>
            {translate('catalog.item_medias_catalog_only')}
          </Typography>
          <MediaGallery
            metadataKey="catalog_item_image"
            metadataValue={itemId}
            accept="image/*"
            readOnly={disabled}
            onMediaChange={syncCatalogMedias}
          />
        </Box>
      )}

      {!itemId && (
        <Typography variant="caption" color="text.secondary">
          {translate('catalog.item_medias_save_item_first')}
        </Typography>
      )}
    </Box>
  );
};

export default CatalogItemMediaEditor;
